<template>
	<div class="lightboxRoot" role="dialog" :aria-label="'Graph viewer: ' + title">
		<div class="lbHeader">
			<div class="lbTitle">{{title}}</div>
			<div class="lbCounter" v-if="items.length > 1">{{index + 1}} / {{items.length}}</div>
			<a ref="closeBtn" role="button" tabindex="0" class="lbClose" aria-label="Close graph viewer"
			   @click="DefaultClose"
			   @keydown.enter.space.prevent="DefaultClose">✕</a>
		</div>
		<div class="lbBody" ref="body">
			<img v-if="displayedSrc" :src="displayedSrc" class="lbImage" :alt="'Graph: ' + title" />
			<div v-if="loading" class="lbLoading"><ScaleLoader /></div>
			<div v-if="loadError" class="lbError">
				The graph failed to render. The Graphite server may be unreachable.
			</div>
			<a v-if="items.length > 1" role="button" tabindex="0" class="lbNav lbPrev" aria-label="Previous graph"
			   @click="step(-1)"
			   @keydown.enter.space.prevent="step(-1)">‹</a>
			<a v-if="items.length > 1" role="button" tabindex="0" class="lbNav lbNext" aria-label="Next graph"
			   @click="step(1)"
			   @keydown.enter.space.prevent="step(1)">›</a>
		</div>
	</div>
</template>

<script>
	// Fullscreen Graphite graph viewer.  The graph is re-requested from the Graphite server at the
	// current viewport size (and device pixel ratio), so resizing the browser window re-renders the
	// graph to match rather than stretching a fixed-size image.
	import { BuildRenderSrc, GetQueryStringTitle } from 'appRoot/scripts/GraphiteRender';

	export default {
		props:
		{
			/** Array of { queryString, serverName } describing the graphs this viewer can page through. */
			items: {
				type: Array,
				required: true
			},
			startIndex: {
				type: Number,
				default: 0
			}
		},
		data()
		{
			return {
				index: 0,
				displayedSrc: null,
				loading: false,
				loadError: false,
				resizeTimeout: null,
				loader: null,
				lastRenderKey: null,
				cacheBust: Date.now()
			};
		},
		created()
		{
			this.index = Math.min(Math.max(0, this.startIndex), this.items.length - 1);
		},
		mounted()
		{
			window.addEventListener("resize", this.onWindowResize);
			window.addEventListener("keydown", this.onKeyDown);
			this.renderCurrent();
		},
		beforeUnmount()
		{
			window.removeEventListener("resize", this.onWindowResize);
			window.removeEventListener("keydown", this.onKeyDown);
			if (this.resizeTimeout)
				clearTimeout(this.resizeTimeout);
			this.abandonLoader();
		},
		computed:
		{
			currentItem()
			{
				return this.items[this.index];
			},
			title()
			{
				let item = this.currentItem;
				if (!item)
					return "";
				return GetQueryStringTitle(item.queryString, item.serverName) || ("Graph " + (this.index + 1));
			}
		},
		methods:
		{
			SetFocus()
			{
				if (this.$refs.closeBtn)
					this.$refs.closeBtn.focus();
			},
			DefaultClose()
			{
				this.$emit("close", false);
			},
			onKeyDown(e)
			{
				if (e.key === "ArrowLeft")
				{
					e.preventDefault();
					this.step(-1);
				}
				else if (e.key === "ArrowRight")
				{
					e.preventDefault();
					this.step(1);
				}
			},
			step(direction)
			{
				if (this.items.length < 2)
					return;
				this.index = (this.index + direction + this.items.length) % this.items.length;
				this.renderCurrent();
			},
			onWindowResize()
			{
				if (this.resizeTimeout)
					clearTimeout(this.resizeTimeout);
				this.resizeTimeout = setTimeout(() => { this.renderCurrent(); }, 350);
			},
			abandonLoader()
			{
				if (this.loader)
				{
					this.loader.onload = this.loader.onerror = null;
					this.loader = null;
				}
			},
			renderCurrent()
			{
				let item = this.currentItem;
				let body = this.$refs.body;
				if (!item || !body)
					return;
				let width = Math.max(200, body.clientWidth - 4);
				let height = Math.max(150, body.clientHeight - 4);
				let renderKey = this.index + "|" + width + "x" + height;
				if (renderKey === this.lastRenderKey && this.displayedSrc && !this.loadError)
					return; // Nothing changed; the resize event fired without an actual size change.
				this.lastRenderKey = renderKey;
				let src = BuildRenderSrc(item.queryString, {
					width, height,
					serverName: item.serverName,
					cacheBust: this.cacheBust
				});
				this.loading = true;
				this.loadError = false;
				this.abandonLoader();
				let loader = this.loader = new Image();
				loader.onload = () =>
				{
					if (loader !== this.loader)
						return;
					this.displayedSrc = src;
					this.loading = false;
				};
				loader.onerror = () =>
				{
					if (loader !== this.loader)
						return;
					this.loading = false;
					this.loadError = true;
				};
				loader.src = src;
			}
		}
	}
</script>

<style scoped>
	.lightboxRoot
	{
		display: flex;
		flex-direction: column;
		width: calc(100vw - 16px);
		height: calc(100vh - 16px);
		box-sizing: border-box;
		background-color: #222222;
		color: #FFFFFF;
	}

	.lbHeader
	{
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 6px 12px;
	}

	.lbTitle
	{
		flex: 1 1 auto;
		font-size: 15px;
		font-weight: bold;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0px;
	}

	.lbCounter
	{
		flex: 0 0 auto;
		font-size: 13px;
		color: #CCCCCC;
	}

	.lbClose
	{
		flex: 0 0 auto;
		cursor: pointer;
		font-size: 18px;
		padding: 2px 10px;
		border-radius: 4px;
	}

		.lbClose:hover, .lbClose:focus
		{
			background-color: rgba(255,255,255,0.15);
		}

	.lbBody
	{
		flex: 1 1 auto;
		position: relative;
		min-height: 0px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}

	.lbImage
	{
		max-width: 100%;
		max-height: 100%;
		width: 100%;
		height: 100%;
		object-fit: contain;
		background-color: #FFFFFF;
	}

	.lbLoading
	{
		position: absolute;
		top: 10px;
		right: 10px;
	}

	.lbError
	{
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		background-color: rgba(170,0,0,0.9);
		padding: 12px 18px;
		border-radius: 6px;
	}

	.lbNav
	{
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		font-size: 42px;
		line-height: 1;
		padding: 18px 12px;
		cursor: pointer;
		color: #333333;
		background-color: rgba(255,255,255,0.55);
		border-radius: 6px;
		user-select: none;
	}

		.lbNav:hover, .lbNav:focus
		{
			background-color: rgba(255,255,255,0.85);
		}

	.lbPrev
	{
		left: 8px;
	}

	.lbNext
	{
		right: 8px;
	}
</style>
